import React, {useEffect, useState} from 'react';
import {useFormik} from 'formik';
import {useNavigate} from 'react-router-dom';
import {useIntl} from 'react-intl';
import {lang} from "../../../../../types/lang";
import {LANGUAGES} from "../../../../../mock/languages";
import {useNotification} from "../../../../../context/NotificationContext";
import {createQuestionCategorySchema} from "../../../../../schema/questionSchema";
import {
  createQuestionCategory,
  getQuestionCategoryById,
  updateQuestionCategory
} from "../../../../../services/questionSvc";
import {UploadImage} from "../../../../../components/common/uploadImage/UploadImage";

interface CategoryFormProps {
  selectedCategoryId: number | null;
  setSelectedCategoryId: (id: number | null) => void;
  refetchQuestionCategory: () => void;
}

const CategoryForm: React.FC<CategoryFormProps> = ({
                                                     selectedCategoryId,
                                                     setSelectedCategoryId,
                                                     refetchQuestionCategory
                                                   }) => {
  const navigate = useNavigate();
  const {formatMessage} = useIntl();
  const {showNotification} = useNotification();
  const [activeTab, setActiveTab] = useState<lang>('uz');

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema: !selectedCategoryId ? createQuestionCategorySchema : null,
    initialValues: {
      name: {
        uz: '',
        ru: '',
        en: '',
      },
      image: null as string | null,
    },
    onSubmit: async (values, {resetForm}) => {
      const formData = new FormData();
      formData.append('name_uz', values.name.uz);
      formData.append('name_ru', values.name.ru);
      formData.append('name_en', values.name.en);

      if (values.image) {
        formData.append('image', values.image);
      }

      try {
        if (selectedCategoryId !== null) {
          const res = await updateQuestionCategory(selectedCategoryId, formData);
          if (res?.success) {
            showNotification('Muvaffaqiyatli', "Tur o'zgartirildi", 'success');
            refetchQuestionCategory();
            setSelectedCategoryId(null);
          }
        } else {
          const res = await createQuestionCategory(formData);
          if (res?.success) {
            showNotification('Muvaffaqiyatli', "Tur qo'shildi", 'success');
            resetForm();
            refetchQuestionCategory();
          }
        }
      } catch (e) {
        showNotification('Xatolik!', '', 'error');
      }
    },
  });

  useEffect(() => {
    if (selectedCategoryId !== null) {
      getQuestionCategoryById(selectedCategoryId).then((category) => {
        if (category) {
          formik.setValues({
            name: {
              uz: category.name_uz,
              ru: category.name_ru,
              en: category.name_en,
            },
            image: null,
          });
        }
      });
    }
  }, [selectedCategoryId, getQuestionCategoryById]);

  const handleTabChange = (lang: lang) => {
    setActiveTab(lang);
  };

  return (
      <div className="card">
        <div className="card-header">
          <h1 className="card-title">Savol-javob turi qo'shish</h1>
          <ul className="nav nav-tabs nav-line-tabs nav-stretch fs-6 border-0">
            {LANGUAGES.map((lang) => (
                <li className="nav-item" key={lang.key}>
                  <button
                      className={`nav-link ${activeTab === lang.key ? 'active' : ''}`}
                      onClick={() => handleTabChange(lang.key as lang)}
                  >
                    {lang.name}
                  </button>
                </li>
            ))}
          </ul>
        </div>
        <div className="card-body">
          <div id="kt_account_profile_details" className="collapse show">
            <form className="form" onSubmit={formik.handleSubmit} encType="multipart/form-data">
              <div className="card-body p-9">
                <div className="row mb-6">
                  <label className="col-lg-4 col-form-label fw-bold fs-6">Banner</label>
                  <div className="col-lg-8 fv-row">
                    <UploadImage
                        key={selectedCategoryId}
                        onImageUpload={(file) => formik.setFieldValue('image', file)}
                        onImageRemove={() => formik.setFieldValue('image', null)}
                        imageSizes="avatar"
                    />
                    <span className="text-muted small">
                    Tavsiya etiladi: <a rel="noreferrer" href="https://3dicons.co/" target="_blank">3dicons.co</a>
                  </span>
                    {formik.touched.image && formik.errors.image && (
                        <div className="text-danger">{formik.errors.image}</div>
                    )}
                  </div>
                </div>
                <div className="row mb-6">
                  <label className="col-lg-4 col-form-label required fw-bold fs-6">Tur nomi</label>
                  <div className="col-lg-8 fv-row">
                    <input
                        type="text"
                        className="form-control form-control-lg form-control-solid"
                        placeholder="Tur nomi"
                        name={`name.${activeTab}`}
                        value={formik.values.name[activeTab]}
                        onChange={formik.handleChange}
                    />
                    {formik.touched.name?.[activeTab] && formik.errors.name?.[activeTab] && (
                        <div className="text-danger">{formik.errors.name[activeTab]}</div>
                    )}
                  </div>
                </div>
              </div>
              <div className="card-footer">
                <div className="justify-content-end d-flex gap-2">
                  <button className="btn btn-secondary" onClick={() => navigate(-1)} type="button">
                    {formatMessage({id: 'Bekor qilish'})}
                  </button>
                  <button disabled={formik.isSubmitting} className="btn btn-primary" type="submit">
                    {formik.isSubmitting ? 'Saqlanmoqda...' : 'Saqlash'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
  );
};

export default CategoryForm;